import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  Award,
  Building2,
  GraduationCap,
  Shield,
  Sparkles,
  ArrowRight,
  Mail,
  BadgeCheck,
} from "lucide-react";
import { EnquiryDialog } from "@/components/EnquiryDialog";

const AboutPage = () => {
  const expertise = [
    {
      icon: Building2,
      title: "Cleaning",
      description:
        "Residential and commercial cleaning across England and Scotland.",
    },
    {
      icon: Users,
      title: "Hospitality",
      description:
        "Trained to maintain the standards demanded by hotels and serviced apartments.",
    },
    {
      icon: GraduationCap,
      title: "Education",
      description:
        "Compliant cleaning protocols for schools, nurseries and universities.",
    },
    {
      icon: Award,
      title: "Corporate",
      description:
        "Office and facility management for fast-growing businesses.",
    },
  ];

  const promises = [
    {
      icon: Shield,
      title: "Vetted & insured",
      copy:
        "Every cleaner passes an enhanced DBS check before their first shift. We carry £2M public liability cover on every job.",
    },
    {
      icon: BadgeCheck,
      title: "Trained, not freelanced",
      copy:
        "Our cleaners are employed, paid above the living wage, and trained to a documented standard — not gig-economy strangers.",
    },
    {
      icon: Sparkles,
      title: "Re-clean guarantee",
      copy:
        "Spot something we missed? Tell us within 24 hours and we'll come back and put it right. Free.",
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              About Clean Kin
            </span>
            <h1
              className="text-5xl md:text-6xl font-bold text-[#1A2E12] tracking-tight mb-6"
              data-testid="about-title"
            >
              Cleaning, done<br />
              <span className="text-[#2D5016]">the way it should be.</span>
            </h1>
            <p className="text-lg text-[#5C6B57] leading-relaxed">
              We&apos;re a UK cleaning company built around two ideas: cleaners deserve to be treated as professionals, and customers deserve to know exactly who&apos;s in their home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="container-clean">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A2E12] mb-6">
                Where Clean Kin started.
              </h2>
              <p className="text-lg text-[#5C6B57] leading-relaxed mb-6">
                Clean Kin started as an idea to offer a service to clients who seek{" "}
                <strong className="text-[#2D5016]">consistency, quality and value</strong>{" "}
                — with an emphasis on{" "}
                <strong className="text-[#2D5016]">eco-friendly products</strong>{" "}
                that are kinder to your home, your family and the planet.
              </p>
              <p className="text-lg text-[#5C6B57] leading-relaxed mb-6">
                We&apos;ve managed cleans at scale across hospitality, education and
                corporate facilities — and we&apos;ve seen exactly where the industry
                lets people down. So we built the company we wanted to hire: vetted
                cleaners on PAYE contracts, trained to a documented standard, paid
                above the London Living Wage, and matched to the same customers every
                visit.
              </p>
              <p className="text-lg text-[#5C6B57] leading-relaxed">
                The result?{" "}
                <strong className="text-[#2D5016]">
                  Cleaners who stay, customers who stay, and standards that don&apos;t slip.
                </strong>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Clean Kin cleaning professional"
                className="rounded-3xl shadow-xl w-full"
                data-testid="about-story-image"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg max-w-[200px]">
                <p className="text-xs text-[#7CB342] font-semibold uppercase tracking-wider mb-1">
                  Eco-friendly
                </p>
                <p className="text-sm text-[#1A2E12]">
                  Plant-based, low-residue products on every clean.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder / Cameo profile */}
      <section className="py-16 bg-white" data-testid="about-cameo">
        <div className="container-clean">
          <div className="max-w-3xl mx-auto bg-[#F9FAF9] rounded-3xl p-8 md:p-10 border border-[#E2E8E0]">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              <img
                src="/founder-cameo.jpg"
                alt="Clean Kin founder"
                className="w-32 h-32 md:w-36 md:h-36 rounded-full object-cover flex-shrink-0 border-4 border-white shadow-lg"
                data-testid="about-cameo-image"
              />
              <div className="flex-1 text-center md:text-left">
                <p className="text-xs text-[#7CB342] font-semibold uppercase tracking-widest mb-2">
                  Meet the founder
                </p>
                <h3 className="text-xl font-semibold text-[#1A2E12] mb-3">
                  The face behind Clean Kin
                </h3>
                <p className="text-[#5C6B57] leading-relaxed text-sm">
                  &ldquo;I started Clean Kin because every cleaner I&apos;d ever hired
                  for my own home eventually let me down — a missed visit, a swap I
                  didn&apos;t agree to, or a steady decline in standards. We built
                  this company to never put another customer in that position.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise grid */}
      <section className="py-16 bg-[#F9FAF9]" data-testid="about-expertise">
        <div className="container-clean">
          <div className="text-center mb-10">
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-3 block">
              Where we&apos;ve operated
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A2E12] tracking-tight">
              Experience that scales beyond your home
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {expertise.map((item) => (
              <Card
                key={item.title}
                className="bg-white border-[#E2E8E0] rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-0">
                  <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#2D5016]" />
                  </div>
                  <h3 className="font-semibold text-[#1A2E12] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#5C6B57]">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Promises (replaces generic values) */}
      <section className="py-24 bg-[#2D5016]">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              Our Promises
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Three things we don&apos;t compromise on.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              These aren&apos;t marketing claims. They&apos;re written into every contract we sign with every cleaner.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {promises.map((p) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
                data-testid={`about-promise-${p.title}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                  <p.icon
                    className="w-7 h-7 text-[#7CB342]"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {p.title}
                </h3>
                <p className="text-white/80 leading-relaxed">{p.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-clean">
          <div className="bg-[#E8F5E9] rounded-3xl p-12 md:p-16 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A2E12] tracking-tight mb-4">
              Ready to see the difference?
            </h2>
            <p className="text-lg text-[#5C6B57] mb-8 max-w-xl mx-auto">
              Get an instant quote in under a minute — or email us if you&apos;d rather talk it through.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button
                  className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6 text-lg btn-primary"
                  data-testid="about-cta-book"
                >
                  Get an Instant Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <EnquiryDialog source="about-cta" subject="General enquiry">
                <Button
                  variant="outline"
                  className="border-[#2D5016] text-[#2D5016] hover:bg-white rounded-full px-8 py-6 text-lg"
                  data-testid="about-cta-email"
                >
                  <Mail className="mr-2 w-5 h-5" />
                  Email Us
                </Button>
              </EnquiryDialog>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
